import React from 'react';
import { get } from 'lodash';

import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import StorageIcon from '@mui/icons-material/Storage';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DnsIcon from '@mui/icons-material/Dns';
// import ComputerIcon from '@mui/icons-material/Computer';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CachedIcon from '@mui/icons-material/Cached';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';

import { FormatBytesM, FormatBits } from './format';
import { FetchAPI } from './auth-service';

class MC2HealthDataService {
    constructor() {
        if(!MC2HealthDataService.instance) {
            MC2HealthDataService.instance = this;
        }

        this.cache = undefined;
        this.getSystemHealth = () => {
            // poor man's cache for now
            const now = new Date().getTime();
            if (this.cache && this.cache.asof + 5000 > now) {
                return Promise.resolve(this.cache.data);
            }

            return FetchAPI({
                method: 'GET',
                url: 'mc2/system/health/',
            }).then(res => {
                this.cache = { asof: new Date().getTime(), data: res };
                return res;
            }).catch(err => {
                console.log(err);
                this.healthcache = undefined;
            });
        };

        this.getHealthHistory = (uuid) => {
            return FetchAPI({
                method: 'GET',
                url: `mc2/system/health/history/`,
            });
        };

        this.getHistoryMetrics = () => {
            return [{
                name: 'issues',
                label: 'Health History',
                icon: <LocalHospitalIcon/>,
                metrics: [
                    {
                        key: 'issue_count',
                        type: 'bar',
                        title: 'Issues',
                        minY: 0,
                        nilValue: 1,
                        value: function (d) {
                        // show a bar regardless
                            var count = get(d, 'issues.issue_count');
                            return count === 0 ? 1 : count;
                        },
                        bad: function (d) {
                            return d.issues ? d.issues.issue_count > 0 : false;
                        },
                        good: function (d) {
                            return d.issues ? d.issues.issue_count < 1 : false;
                        },
                        tipFormat: function (v, d) {
                            if (d.issues.issue_count > 0) {
                                const msg = d.issues.issue_msg
                                    ? d.issues.issue_msg.split(',').map(s => [s.trim(), ''])
                                    : 'CMC health issue' + (d.issues.issue_count > 1 ? 's' : '');
                                return msg;
                            }
                            return 'no issues reported';
                        }
                    }]
            },
            {
                name: 'system',
                label: 'System',
                icon: <DnsIcon/>,
                metrics: [
                    {
                        key: 'avg_used_cpu_pct',
                        type: 'line',
                        minY: 0,
                        title: 'Avg. CPU Usage',
                        value: d => get(d, 'system.avg_used_cpu_pct'),
                        tipFormat: (v, d) => usedCpu(v)
                    },
                    {
                        key: 'avg_avail_mem_mb',
                        type: 'line',
                        minY: 0,
                        title: 'Avg. Avail. Mem',
                        value: d => get(d, 'system.avg_avail_mem_mb'),
                        tipFormat: (v, d) => availMem(v)
                    },
                    {
                        key: 'avg_load_1m',
                        type: 'line',
                        minY: 0,
                        title: 'Load Avg. 1 min.',
                        value: d => get(d, 'system.avg_load_1m'),
                        tipFormat: (v, d) => loadAvg(v)
                    },
                    {
                        key: 'avg_load_5m',
                        type: 'line',
                        minY: 0,
                        title: 'Load Avg. 5 min.',
                        value: d => get(d, 'system.avg_load_5m'),
                        tipFormat: (v, d) => loadAvg(v)
                    },
                    {
                        key: 'avg_load_15m',
                        type: 'line',
                        minY: 0,
                        title: 'Load Avg. 15 min.',
                        value: d => get(d, 'system.avg_load_15m'),
                        tipFormat: (v, d) => loadAvg(v)
                    }
                ]
            },
            {
                name: 'dbwd',
                label: 'DB',
                icon: <StorageIcon/>,
                metrics: [
                    {
                        key: 'db_fill',
                        type: 'line',
                        title: 'Fill',
                        minY: 0,
                        maxY: 1,
                        value: d => get(d, 'dbwd.db_fill'),
                        tipFormat: (v, d) => percentFilter(v)
                    }
                ]
            },
            {
                name: 'disks',
                label: 'Disks',
                icon: <ViewModuleIcon/>,
                nested: true,
                metrics: [
                    {
                        key: 'avg_utilization',
                        type: 'line',
                        title: 'Utilization',
                        minY: 0,
                        maxY: 1,
                        tipFormat: (v, d) => percentFilter(v)
                    }
                ]
            },
            {
                name: 'apps',
                label: 'Applications',
                icon: <CachedIcon/>,
                nested: true,
                metrics: [
                    {
                        key: 'avg_pct_cpu',
                        type: 'line',
                        title: 'CPU%',
                        minY: 0,
                        maxY: 100,
                        value: () => {},
                        tipFormat: (v, d) => usedCpu(v)
                    },
                    {
                        key: 'avg_rss',
                        type: 'line',
                        title: 'RSS',
                        value: () => {},
                        tipFormat: (v, d) => kBytesTipFilter(v)
                    },
                    {
                        key: 'avg_procs',
                        minY: 0,
                        type: 'line',
                        title: 'Procs'
                    },
                    {
                        key: 'avg_threads',
                        minY: 0,
                        type: 'line',
                        title: 'Threads'
                    },
                    {
                        key: 'avg_files',
                        minY: 0,
                        type: 'line',
                        title: 'Files'
                    }
                ]
            },
            {
                name: 'aws_stats',
                label: 'AWS Stats',
                icon: <CloudCircleIcon/>,
                metrics: [
                    {
                        key: 'avg_used_cpu_pct',
                        type: 'line',
                        title: 'Avg. CPU Usage',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_used_cpu_pct'),
                        tipFormat: (v, d) => usedCpu(v)
                    },
                    {
                        key: 'avg_disk_read_ops',
                        type: 'line',
                        title: 'Avg. Disk Read Ops',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_read_ops'),
                        tipFormat: (v, d) => opsTipFilter(v)
                    },
                    {
                        key: 'avg_disk_write_ops',
                        type: 'line',
                        title: 'Avg. Disk Write Ops',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_write_ops'),
                        tipFormat: (v, d) => opsTipFilter(v)
                    },
                    {
                        key: 'avg_disk_read_bytes',
                        type: 'line',
                        title: 'Avg. Disk Read Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_read_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_disk_write_bytes',
                        type: 'line',
                        title: 'Avg. Disk Write Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_disk_write_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_nw_in_bytes',
                        type: 'line',
                        title: 'Avg. Network In Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_in_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_nw_out_bytes',
                        type: 'line',
                        title: 'Avg. Network Out Bytes',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_out_bytes'),
                        tipFormat: (v, d) => BytesTipFilter(v)
                    },
                    {
                        key: 'avg_nw_pkts_in',
                        type: 'line',
                        title: 'Avg. Network Packets In',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_pkts_in'),
                        tipFormat: (v, d) => TxBitsTipFilter(v)
                    },
                    {
                        key: 'avg_nw_pkts_out',
                        type: 'line',
                        title: 'Avg. Network Packets Out',
                        minY: 0,
                        value: d => get(d, 'aws_stats.avg_nw_pkts_out'),
                        tipFormat: (v, d) => TxBitsTipFilter(v)
                    }
                ]
            }];
        }

        return MC2HealthDataService.instance;
    }
}

// converts Bytes/s to most reasonable bps unit up to Gbps
// returns string with unit
function TxBytesTipFilter (value) { return FormatBytesM(value, 1) + '/s' }

function TxBitsTipFilter (value) { return FormatBits(value, 1) + '/s' }

function kBytesTipFilter (value) { return FormatBytesM(value * 1000, 1) }

// returns string with unit packets/s
function TxPktsTipFilter (value) { return value.toFixed(2) + ' p/s' }

// displays friendly alerts tooltips
function alertsTipFilter (value) { return value.toFixed(2) + ' alerts p/m' }

function Alerts2TipFilter (value) { return value.toFixed(2) + ' alerts' }

function ErrorsTipFilter (value) { return value.toFixed(2) + ' errors p/m' }

function FilesTipFilter (value) { return value.toFixed(2) + ' files p/m' }

// displays friendly logs tooltips
function logsTipFilter (value) { return value.toFixed(2) + ' logs p/s' }

function Logs2TipFilter (value) { return value.toFixed(2) + ' logs' }

// displays percent tooltips
function usedCpu (value) { return (value * 1).toFixed(2) + '%' }

// displays friendly avail memory tooltips
function availMem (value) { return parseInt(value, 10) + ' MB' }

// displays friendly load avg tooltips
function loadAvg (value) { return value.toFixed(2) + ' Load avg' }

// displays friendly code temp
function CoreTemp (value) { return value.toFixed(2) + ' °C' }

// displays percent tooltips
function percentFilter (value) { return (value * 100).toFixed(2) + '%' }

// returns string with unit operations/second = Op/s??
function opsTipFilter (value) { return value.toFixed(2) + ' Op/s' }

function BytesTipFilter (value) { return FormatBytesM(value, 1) + '/s' }

function DocsTipFilter (value) {
    return value.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' documents';
}

function IsVM (model, machineType) {
    // new approach as of 4.0.0 to use machine type
    if (machineType && machineType.length > 0) {
        return machineType.toLowerCase() !== 'physical';
    }

    // this is a very brittle approach
    // fallback for sensors older than 4.0.0
    return [
        'VMware', 'VMsmall', 'VMpacketd1', 'VMpacketd2', 'VGX-1R1',
        'VGX-1P1', 'VGX-10R1', 'VSA-S1', 'VMA-S1', 'CMCVMware'
    ].indexOf(model) >= 0;
}

const MC2HealthService = new MC2HealthDataService();

export {
    MC2HealthService as default,
    IsVM,
    TxBitsTipFilter,
    BytesTipFilter,
    CoreTemp,
    Logs2TipFilter,
    FilesTipFilter,
    ErrorsTipFilter,
    Alerts2TipFilter,
    TxPktsTipFilter,
    TxBytesTipFilter,
    DocsTipFilter,
};
